import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function FashionItemPublic() {
  const { kind, id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", quantity: 1 });
  const [busy, setBusy] = useState(false);
  const [ordered, setOrdered] = useState(null);

  useEffect(() => {
    api.get(`/fashion/public/item/${kind}/${id}`)
      .then(({ data }) => setItem(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not found"));
  }, [kind, id]);

  useSeo({
    title: item ? `${item.title} | Unity & Utility` : "Fashion trends | Unity & Utility",
    description: (item?.body || "Workwear, uniform and textile trends from a working industrial supplier.").slice(0, 180),
    path: `/fashion-trends/${kind}/${id}`,
    image: item?.image_url ? `${BACKEND}${item.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Fashion trends", "/fashion-trends"], [item?.title || "Item", null]]),
  });

  const reserve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fashion/public/order", {
        listing_id: item.listing_id, name: form.name.trim(), email: form.email.trim(),
        quantity: Number(form.quantity) });
      setOrdered(data);
      toast.success(`${data.title} reserved — ${data.currency} ${data.total}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const shareUrl = `${BACKEND}/api/share/fashion/${kind}/${id}`;

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="fashion-item-page">
        <Crumbs items={[["Home", "/"], ["Fashion trends", "/fashion-trends"], [item?.title || "Item", null]]} />
        {error && <p data-testid="fashion-item-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {item && (
          <>
            <p className="overline text-[#B3241B]">{LABEL(item.segment)}</p>
            <h1 data-testid="fashion-item-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {item.title}
            </h1>
            {item.image_url && (
              <img data-testid="fashion-item-image" src={`${BACKEND}${item.image_url}`} alt={item.title}
                className="mt-6 w-full border border-border object-cover" />
            )}
            {item.kind === "listing" && (
              <p data-testid="fashion-item-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
                {item.currency} {item.price}
                <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {item.quantity} {item.unit} available{item.seller ? ` · ${item.seller}` : ""}
                </span>
              </p>
            )}
            {item.body && <p data-testid="fashion-item-body" className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{item.body}</p>}

            <div className="mt-7">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Share this picture</p>
              <div className="mt-2">
                <ShareBar url={shareUrl} text={item.title} testid="fashion-item-share" instagram />
              </div>
            </div>

            {item.kind === "listing" && (
              <div className="mt-8 border border-border bg-white p-5" data-testid="fashion-item-buy">
                {ordered ? (
                  <p data-testid="fashion-item-order-done" className="border-l-2 border-[#0F7A3D] bg-[#EAF7EF] px-4 py-3 text-sm text-[#0B5C2E]">
                    {ordered.title} reserved — {ordered.currency} {ordered.total}. {ordered.message}
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-[#0F172A]">Buy this</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      <div><Label className="text-xs">Your name</Label><Input data-testid="fashion-item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                      <div><Label className="text-xs">Email</Label><Input data-testid="fashion-item-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                      <div><Label className="text-xs">How many</Label><Input data-testid="fashion-item-qty" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                    </div>
                    <Button className="mt-4 w-full sm:w-auto" data-testid="fashion-item-buy-submit" onClick={reserve}
                      disabled={busy || form.name.trim().length < 2 || !form.email.includes("@")}>
                      <ShoppingCart size={16} className="mr-1.5" /> {busy ? "Reserving…" : "Reserve it"}
                    </Button>
                    <p className="mt-2 text-[11px] text-slate-500">The seller emails you a payment link — nothing is charged now.</p>
                  </>
                )}
              </div>
            )}

            <Link to="/fashion-trends" data-testid="fashion-item-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See everything selling this season
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
